import React, { useState, useEffect } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import classNames from 'classnames';
import { Icon } from 'views/components/Icon/Icon';
import { Button } from 'views/components/Button/Button';
import { PromoCodeDiscount } from 'types';

interface Props {
  intl: IntlShape;
  onApplyCode: (promoCode: string) => void;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage?: string;
  defaultValue?: string;
  promoCodeDiscount?: PromoCodeDiscount;
}

const PromoCodeInputView = (props: Props) => {
  const {
    onApplyCode,
    intl,
    isLoading,
    isSuccess,
    isError,
    errorMessage,
    promoCodeDiscount,
    defaultValue,
  } = props;

  const promoCodeDiscountCode = promoCodeDiscount?.code;

  const [promoCode, setPromoCode] = useState(promoCodeDiscountCode || '');
  const isDisabled = !promoCode || promoCodeDiscountCode === promoCode;

  useEffect(() => {
    if (defaultValue) {
      setPromoCode(defaultValue);
    }
  }, [promoCodeDiscount, defaultValue, setPromoCode]);

  const applyCode = () => {
    if (!promoCode) {
      return;
    }

    onApplyCode(promoCode);
  };

  return (
    <div className="PromoCodeInput">
      <legend>
        <FormattedMessage id="PROMO_CODE" />
      </legend>

      <div className="PromoCodeInput-container">
        <label className="PromoCodeInput-label" htmlFor="promoCode">
          <span className="u-hiddenVisually">
            <FormattedMessage id="PROMO_CODE_PLACEHOLDER" />
          </span>

          <input
            id="promoCode"
            className={classNames('PromoCodeInput-input', { hasError: isError })}
            type="text"
            value={promoCode}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                applyCode();
              }
            }}
            onChange={(e) => {
              setPromoCode(e.target.value || '');
            }}
            placeholder={intl.formatMessage({ id: 'PROMO_CODE_PLACEHOLDER' })}
          />

          {/* Screen reader error message */}
          {isError && errorMessage && (
            <span className="u-hiddenVisually" aria-live="polite">
              {errorMessage}
            </span>
          )}

          <div className="PromoCodeInput-icon">
            {isLoading && (
              <div className="PromoCodeInput-loader">
                <div className="Spinner">
                  <i /><i /><i /><i /><i /><i />
                </div>
              </div>
            )}

            {isSuccess && (
              <Icon name="ic-tick" />
            )}

            {isError && (
              <Icon name="ic-exclamation" />
            )}
          </div>
        </label>

        <Button
          className={classNames('Button PromoCodeInput-button', { isLoading, isDisabled })}
          onClick={() => applyCode()}
          label={intl.formatMessage({ id: 'APPLY_CODE' })}
          isDisabled={isDisabled}
        />

      </div>

      {/* UI visible error message */}
      {isError && errorMessage && (
        <div className="PromoCodeInput-error" aria-hidden>
          <span>{errorMessage}</span>
        </div>
      )}

    </div>
  );
};

export const PromoCodeInput = injectIntl(PromoCodeInputView);
